package one.healthium.content.navigation.main

import com.arkivanov.decompose.ComponentContext
import com.arkivanov.decompose.router.stack.ChildStack
import com.arkivanov.decompose.router.stack.StackNavigation
import com.arkivanov.decompose.router.stack.childStack
import com.arkivanov.decompose.router.stack.childStackWebNavigation
import com.arkivanov.decompose.router.stack.pop
import com.arkivanov.decompose.router.stack.pushNew
import com.arkivanov.decompose.router.webhistory.WebNavigation
import com.arkivanov.decompose.value.Value
import kotlinx.serialization.Serializable
import one.healthium.content.navigation.content.DefaultContentComponent
import one.healthium.content.navigation.insights.DefaultInsightsComponent
import one.healthium.content.navigation.shopping_list.DefaultShoppingListComponent
import one.healthium.insights.domain.content.ContentOptions
import one.healthium.insights.domain.content.ContentOptions.Category
import one.healthium.insights.domain.content.ContentOptions.Insight
import one.healthium.insights.domain.content.ContentOptions.MealPlan
import one.healthium.insights.domain.content.ContentOptions.Recipe
import one.healthium.insights.domain.content.ContentOptions.Workout
import one.healthium.insights.domain.models.ActionModel
import one.healthium.insights.domain.models.ShoppingListModel

class DefaultMainContentComponent(
    componentContext: ComponentContext,
    private val onActionClicked: (ActionModel) -> Unit,
) : MainContentComponent, ComponentContext by componentContext {

    private val nav = StackNavigation<Config>()

    private val _stack = childStack(
        source = nav,
        serializer = Config.serializer(),
        initialStack = { listOf(Config.Insights) },
        childFactory = ::child,
    )

    private fun child(config: Config, componentContext: ComponentContext): MainContentComponent.Child {
        return when (config) {
            Config.Insights -> MainContentComponent.Child.Insights(
                DefaultInsightsComponent(
                    componentContext = componentContext,
                    onActionClicked = ::onActionClicked,
                )
            )

            is Config.Content -> MainContentComponent.Child.Content(
                DefaultContentComponent(
                    componentContext = componentContext,
                    options = config.options,
                    onBackClicked = ::onBackClicked,
                    onActionClicked = ::onActionClicked,
                )
            )

            is Config.ShoppingList -> MainContentComponent.Child.ShoppingList(
                DefaultShoppingListComponent(
                    componentContext = componentContext,
                    shoppingList = config.shoppingList,
                    onBackClicked = ::onBackClicked,
                )
            )
        }
    }

    private fun onActionClicked(action: ActionModel) {
        when (action) {
            is ActionModel.Insight -> nav.pushNew(Config.Content(Insight(action.id)))
            is ActionModel.Recipe -> nav.pushNew(Config.Content(Recipe(action.id)))
            is ActionModel.Category -> nav.pushNew(Config.Content(Category(action.id)))
            is ActionModel.Workout -> nav.pushNew(Config.Content(Workout(action.id)))
            is ActionModel.ShoppingList -> nav.pushNew(Config.ShoppingList(action.shoppingList))
            is ActionModel.MealPlan -> nav.pushNew(Config.Content(MealPlan(action.id)))
            else -> onActionClicked.invoke(action)
        }
    }

    override val stack: Value<ChildStack<*, MainContentComponent.Child>> = _stack

    override fun onBackClicked() {
        nav.pop()
    }

    override val webNavigation: WebNavigation<*> = childStackWebNavigation(
        navigator = nav,
        stack = _stack,
        serializer = Config.serializer(),
    )

    @Serializable
    private sealed interface Config {
        @Serializable
        data object Insights : Config

        @Serializable
        data class Content(
            val options: ContentOptions,
        ) : Config

        @Serializable
        data class ShoppingList(
            val shoppingList: ShoppingListModel,
        ) : Config
    }
}